import React, { useState } from 'react'
import DownloadIcon from '@material-ui/icons/Archive'
import EditIcon from '@material-ui/icons/Edit'
import VisibilityIcon from '@material-ui/icons/Visibility'

import moment from 'config/moment'
import { ERRORS, STRINGS } from 'consts'
import { useApi, useApiCall } from 'hooks'
import { snackbar } from 'components'
import { createDateSort } from 'utils'

import { getCampaigns, getCampaignUsersEmails } from './api'
import { DefaultTable, TagsIndicator } from '../components'
import { GetCampaignsResponse, Campaign, GetCampaignUsersEmailsResponse } from './types'
import { CreateCampaignDialog, DeleteCampaignDialog, UpdateCampaignDialog } from './components'
import { getActiveTags } from '../Tags/api'
import { GetTagsResponse, Tag } from '../Tags/types'
import { downloadCSV, formatCampaignEmailsCSV } from '../Downloads/utils/parseCsv'

const columns = [
  { title: 'ID', field: 'id' },
  { title: 'Título', field: 'title' },
  { title: 'Descripción', field: 'description' },
  {
    title: 'Fecha inicio',
    field: 'startDate',
    render: (rowData: Campaign) =>
      rowData.startDate ? moment(rowData.startDate).format(STRINGS.DATE_FORMAT) : '-',
    customSort: createDateSort<Campaign>(row => row.startDate),
  },
  {
    title: 'Fecha fin',
    field: 'endDate',
    render: (rowData: Campaign) =>
      rowData.endDate ? moment(rowData.endDate).format(STRINGS.DATE_FORMAT) : '-',
    customSort: createDateSort<Campaign>(row => row.endDate),
  },
  {
    title: 'Premio activo',
    render: (rowData: Campaign) => (rowData.activeReward ? 'Sí' : 'No'),
    sorting: false,
  },
  {
    title: 'Push activa',
    render: (rowData: Campaign) => (rowData.activePush ? 'Sí' : 'No'),
    sorting: false,
  },
  {
    title: 'Tags',
    render: (rowData: Campaign) => <TagsIndicator tags={rowData.tags} />,
    sorting: false,
  },
  {
    title: 'Programada',
    field: 'startDate',
    render: (rowData: Campaign) => {
      const startDate = moment(rowData.startDate)
      const now = moment()
      if (startDate.isAfter(now)) {
        return (
          <span style={{ color: 'red' }}>
            {moment(rowData.startDate).format('DD/MM/YYYY HH:mm')}
          </span>
        )
      }
      return moment(rowData.startDate).format('DD/MM/YYYY HH:mm')
    },
    customSort: createDateSort<Campaign>(row => row.startDate),
  },
  {
    title: 'Procesamiento iniciado',
    field: 'processingStartedAt',
    render: (rowData: Campaign) =>
      rowData.processingStartedAt
        ? moment(rowData.processingStartedAt).format(STRINGS.DATE_TIME_FORMAT)
        : '-',
    customSort: createDateSort<Campaign>(row => row.processingStartedAt),
  },
  {
    title: 'Completada',
    field: 'processingCompletedAt',
    render: (rowData: Campaign) =>
      rowData.processingCompletedAt
        ? moment(rowData.processingCompletedAt).format(STRINGS.DATE_TIME_FORMAT)
        : '-',
    customSort: createDateSort<Campaign>(row => row.processingCompletedAt),
  },
]

const getCampaignsResponseGetter = (responseData: GetCampaignsResponse) =>
  responseData?.campaigns ?? []
const getTagsResponseGetter = (responseData: GetTagsResponse) => responseData?.tags ?? []

const CampaignsList: React.FC = () => {
  const [{ data: campaigns, isLoading }, , setCampaigns] = useApi<GetCampaignsResponse, Campaign[]>(
    getCampaigns,
    getCampaignsResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )
  const [{ data: tags }] = useApi<GetTagsResponse, Tag[]>(getActiveTags, getTagsResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })
  const [getCampaignUsersEmailsApi] = useApiCall<number, GetCampaignUsersEmailsResponse>(
    getCampaignUsersEmails,
  )

  const [isCreateCampaignDialogOpen, setIsCreateCampaignDialogOpen] = useState(false)
  const [isDeleteCampaignDialogOpen, setIsDeleteCampaignDialogOpen] = useState(false)
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null)
  const [isUpdateCampaignDialogOpen, setIsUpdateCampaignDialogOpen] = useState(false)
  const [campaignToUpdate, setCampaignToUpdate] = useState<Campaign | null>(null)
  const [isViewCampaignDialogOpen, setIsViewCampaignDialogOpen] = useState(false)
  const [campaignToView, setCampaignToView] = useState<Campaign | null>(null)

  const handleCreateCampaignDone = (newCampaign: Campaign) => {
    setCampaigns([newCampaign, ...campaigns])
    setIsCreateCampaignDialogOpen(false)
  }

  const handleDeleteCampaignDialogClose = () => {
    setIsDeleteCampaignDialogOpen(false)
    setCampaignToDelete(null)
  }

  const handleDeleteCampaignDone = () => {
    if (!campaignToDelete) return
    setCampaigns(campaigns.filter(campaign => campaign.id !== campaignToDelete.id))
    handleDeleteCampaignDialogClose()
  }

  const handleUpdateCampaignDialogClose = () => {
    setIsUpdateCampaignDialogOpen(false)
    setCampaignToUpdate(null)
  }

  const handleUpdateCampaignDone = (updatedCampaign: Campaign) => {
    setCampaigns(
      campaigns.map(campaign => {
        if (campaign.id === updatedCampaign.id) {
          return updatedCampaign
        }
        return campaign
      }),
    )
    handleUpdateCampaignDialogClose()
  }

  const handleViewCampaignDialogClose = () => {
    setIsViewCampaignDialogOpen(false)
    setCampaignToView(null)
  }

  const handleDownloadCampaignEmails = async (campaign: Campaign): Promise<void> => {
    const { emails } = await getCampaignUsersEmailsApi(campaign.id)
    if (!emails.length) {
      snackbar.show(ERRORS.NO_DATA_ERROR_MESSAGE)
      return
    }

    downloadCSV(formatCampaignEmailsCSV(emails), `campaign-${campaign.id}-emails`)
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Crear nueva campaña',
      isFreeAction: true,
      onClick: () => setIsCreateCampaignDialogOpen(true),
    },
    () => ({
      icon: () => <VisibilityIcon />,
      tooltip: 'Visualizar campaña',
      disabled: false,
      onClick: (_: React.SyntheticEvent, rowData: Campaign | Campaign[]) => {
        const campaign = rowData as Campaign
        setCampaignToView(campaign)
        setIsViewCampaignDialogOpen(true)
      },
    }),
    () => ({
      icon: () => <EditIcon />,
      tooltip: 'Editar campaña',
      disabled: false,
      onClick: (_: React.SyntheticEvent, rowData: Campaign | Campaign[]) => {
        const campaign = rowData as Campaign
        setCampaignToUpdate(campaign)
        setIsUpdateCampaignDialogOpen(true)
      },
    }),
    () => ({
      icon: () => <DownloadIcon />,
      tooltip: 'Descargar emails',
      disabled: false,
      onClick: (_: React.SyntheticEvent, rowData: Campaign | Campaign[]) => {
        const campaign = rowData as Campaign
        handleDownloadCampaignEmails(campaign).catch(() => {
          snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
        })
      },
    }),
  ]

  return (
    <>
      <DefaultTable
        title=""
        columns={columns}
        data={campaigns}
        isLoading={isLoading}
        actions={actions}
      />
      {isCreateCampaignDialogOpen && tags && (
        <CreateCampaignDialog
          handleClose={() => setIsCreateCampaignDialogOpen(false)}
          onDone={handleCreateCampaignDone}
          tags={tags}
        />
      )}
      {isDeleteCampaignDialogOpen && campaignToDelete && (
        <DeleteCampaignDialog
          handleClose={handleDeleteCampaignDialogClose}
          campaignId={campaignToDelete.id}
          campaignTitle={`Campaña #${campaignToDelete.id}`}
          onDone={handleDeleteCampaignDone}
        />
      )}
      {isUpdateCampaignDialogOpen && campaignToUpdate && tags && (
        <UpdateCampaignDialog
          handleClose={handleUpdateCampaignDialogClose}
          campaignToUpdate={campaignToUpdate}
          onDone={handleUpdateCampaignDone}
          tags={tags}
        />
      )}
      {isViewCampaignDialogOpen && campaignToView && tags && (
        <UpdateCampaignDialog
          handleClose={handleViewCampaignDialogClose}
          campaignToUpdate={campaignToView}
          onDone={handleViewCampaignDialogClose}
          tags={tags}
          isReadOnly
        />
      )}
    </>
  )
}

export { CampaignsList }
